import React, { useContext, useEffect, useState } from 'react';
import { compose } from 'recompose';
import { css } from '@emotion/react';
import { Checkbox, Collapse, Grid, IconButton, TextField, Tooltip } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { DeleteForeverOutlined, ExpandLess, ExpandMore } from '@material-ui/icons';

import { colors } from '@/styles';
import { PageTitle, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Body1 } from '@/components/Account/blocks';
import Snackbar from '@/components/Snackbar';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { TAG_TYPES } from '@/components/Account/CustomTags/constants';
import withAdminRights from '@/components/Auth/withAdminRights';

import { Context, ContextProvider } from './store';

const TYPE_OPTIONS = Object.entries(TAG_TYPES).map(([value, label]) => ({ value, label }));

// "Always Apply To" search
const accountFilter = createFilterOptions({
    matchFrom: 'any',
    ignoreCase: true,
    stringify: (option) => `${option.label} ${option.roles || ''}`,
});

const rowCss = css`
    flex-wrap: nowrap;
    align-items: center;
    margin-top: 1rem;
`;

const fieldCss = css`
    width: 300px;
    margin-right: 1rem;
`;

const sectionCss = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const TagRow = ({ tag, onChange, onDelete, accountOptions }) => {
    const [name, setName] = useState(tag.tag || '');
    useEffect(() => setName(tag.tag || ''), [tag.tag]);

    const selectedTypes = TYPE_OPTIONS.filter((opt) => tag[opt.value]);
    const alwaysApplyClientIds = (tag.always_apply_clients || []).map((c) => c.client_id);
    const selectedAccounts = (accountOptions || []).filter((opt) => alwaysApplyClientIds.includes(opt.client_id));

    return (
        <Grid container css={rowCss}>
            <Grid item css={fieldCss}>
                <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    label="Tag"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => {
                        if (name !== tag.tag) onChange({ ...tag, tag: name });
                    }}
                />
            </Grid>
            <Grid item css={fieldCss}>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    value={selectedTypes}
                    options={TYPE_OPTIONS}
                    getOptionSelected={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option.label}
                    onChange={(e, options) => {
                        const selected = Object.fromEntries(options.map((option) => [option.value, true]));
                        onChange({
                            ...tag,
                            ...Object.fromEntries(Object.keys(TAG_TYPES).map((type) => [type, !!selected[type]])),
                        });
                    }}
                    renderTags={(options) => <span>{options.length} Type(s) selected</span>}
                    renderOption={(option, { selected }) => (
                        <li key={option.value}>
                            <Checkbox color="primary" checked={selected} />
                            {option.label}
                        </li>
                    )}
                    renderInput={(params) => <TextField variant="outlined" {...params} label="Types" />}
                />
            </Grid>
            {accountOptions && (
                <Grid item css={fieldCss}>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        size="small"
                        options={accountOptions}
                        value={selectedAccounts}
                        filterOptions={accountFilter}
                        getOptionSelected={(option, value) => option.client_id === value.client_id}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, selected) =>
                            onChange({
                                ...tag,
                                always_apply_clients: selected.map((s) => ({
                                    client_id: s.client_id,
                                    organization_id: s.organization_id || null,
                                })),
                            })
                        }
                        renderTags={(opts) => <span>({opts.length})</span>}
                        renderOption={(option, { selected }) => (
                            <li key={option.client_id}>
                                <Checkbox color="primary" checked={selected} />
                                {option.label}
                                {option.roles ? ` (${option.roles})` : ''}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                variant="outlined"
                                {...params}
                                label="Always Apply To"
                                placeholder="Search name"
                                inputProps={{
                                    ...params.inputProps,
                                    onKeyDown: (e) => {
                                        // Swallow backspace if no input
                                        // stops removal of last selected
                                        if (e.key === 'Backspace' && !e.target.value) {
                                            e.stopPropagation();
                                            return;
                                        }
                                        params.inputProps.onKeyDown?.(e);
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
            )}
            <Grid item>
                <IconButton onClick={() => onDelete(tag)}>
                    <DeleteForeverOutlined
                        css={css`
                            color: ${colors.reds.primary};
                        `}
                    />
                </IconButton>
            </Grid>
        </Grid>
    );
};

const DeleteModal = ({ pending, onCancel, onConfirm }) => (
    <NavResponsiveModal open={!!pending} onClose={onCancel}>
        <ModalHeader title="Delete Tag?" onClose={onCancel} />
        <ModalContent>
            <Body1>Are you sure you want to delete "{pending?.tag?.tag}"? </Body1>
            {pending?.count > 0 && (
                <Body1
                    css={css`
                        margin-top: 1rem;
                        color: ${colors.reds.primary};
                    `}
                >
                    This tag is currently applied to {pending.count} order(s)/service level(s). Deleting it removes
                    those associations.
                </Body1>
            )}
        </ModalContent>
        <ModalActions>
            <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
            <PrimaryButton onClick={onConfirm}>Delete</PrimaryButton>
        </ModalActions>
    </NavResponsiveModal>
);

const sectionTitleCss = css`
    font-weight: 700;
    font-size: 1.1rem;
`;

const SectionTitle = ({ children }) => (
    <Body1
        css={css`
            ${sectionTitleCss};
            margin-top: 2.5rem;
        `}
    >
        {children}
    </Body1>
);

const CollapsibleSection = ({ title, description, defaultExpanded = false, children }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [hasOpened, setHasOpened] = useState(defaultExpanded);

    const toggle = () =>
        setExpanded((prev) => {
            if (!prev) setHasOpened(true);
            return !prev;
        });

    return (
        <div css={sectionCss}>
            <div
                onClick={toggle}
                css={css`
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    user-select: none;
                    margin-top: 2.5rem;
                `}
            >
                <Body1 css={sectionTitleCss}>{title}</Body1>
                <IconButton size="small" aria-label={expanded ? 'Collapse section' : 'Expand section'}>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </div>
            <Body1>{description}</Body1>
            <Collapse
                in={expanded}
                css={css`
                    width: 100%;
                `}
            >
                {hasOpened ? children() : null}
            </Collapse>
        </div>
    );
};

// Account headers and their tags
const ClientTagGroup = ({ client, tags, onChange, onDelete, onAdd }) => (
    <div
        css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-top: 2rem;
        `}
    >
        <Body1
            css={css`
                font-weight: 600;
            `}
        >
            {client.business_name || client.email || client.client_id}
        </Body1>
        {tags.map((tag) => (
            <TagRow key={tag.tag_id} tag={tag} onChange={onChange} onDelete={onDelete} />
        ))}
        <SecondaryButton
            onClick={onAdd}
            css={css`
                margin-top: 1rem;
            `}
        >
            Add Tag
        </SecondaryButton>
    </div>
);

const CorporateTagsInner = () => {
    const { state, callbacks } = useContext(Context);
    const {
        orgTags,
        tagsByClient,
        shipperClients,
        carrierClients,
        accountOptions,
        organization_id,
        notification,
        pendingDelete,
        hasEdits,
        saving,
    } = state;

    const renderAccounts = (list) =>
        list.length ? (
            list.map((client) => (
                <ClientTagGroup
                    key={client.client_id}
                    client={client}
                    tags={tagsByClient[client.client_id] || []}
                    onChange={callbacks.stageEdit}
                    onDelete={callbacks.requestDelete}
                    onAdd={() => callbacks.createClientTag(client.client_id)}
                />
            ))
        ) : (
            <Body1
                css={css`
                    margin-top: 1rem;
                `}
            >
                No accounts.
            </Body1>
        );

    return (
        <div
            css={css`
                padding: 2rem 5rem;
            `}
        >
            <Snackbar
                open={!!notification.message}
                {...notification}
                handleClose={() => callbacks.setNotification({})}
            />

            <div css={sectionCss}>
                <PageTitle>Tags</PageTitle>
                <PrimaryButton
                    onClick={callbacks.saveEdits}
                    disabled={!hasEdits || saving}
                    css={css`
                        margin-top: 1rem;
                    `}
                >
                    {saving ? 'Saving…' : 'Save Tags'}
                </PrimaryButton>
            </div>

            <div css={sectionCss}>
                <SectionTitle>Organization Tags</SectionTitle>
                <Body1>Available to every account in your organization.</Body1>
                {orgTags.map((tag) => (
                    <TagRow
                        key={tag.tag_id}
                        tag={tag}
                        onChange={callbacks.stageEdit}
                        onDelete={callbacks.requestDelete}
                        accountOptions={accountOptions}
                    />
                ))}
                <Tooltip
                    title={
                        !organization_id ? 'Your account is not associated with an organization — contact support.' : ''
                    }
                >
                    <span>
                        <PrimaryButton
                            disabled={!organization_id}
                            onClick={callbacks.createOrgTag}
                            css={css`
                                margin-top: 1rem;
                            `}
                        >
                            Add Org Tag
                        </PrimaryButton>
                    </span>
                </Tooltip>
            </div>

            <CollapsibleSection
                title="Shipper Accounts"
                description="Each shipper's own tags. Editing or deleting affects only that account."
            >
                {() => renderAccounts(shipperClients)}
            </CollapsibleSection>

            <CollapsibleSection
                title="Carrier Accounts"
                description="Each carrier's own tags. Editing or deleting affects only that account."
            >
                {() => renderAccounts(carrierClients)}
            </CollapsibleSection>

            <DeleteModal
                pending={pendingDelete}
                onCancel={callbacks.cancelDelete}
                onConfirm={callbacks.confirmDelete}
            />
        </div>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withAdminRights, withContext)(CorporateTagsInner);
